import { useEffect, useState } from "react";
import { X, Share2, Plus } from "lucide-react";
import { iosNeedsInstall } from "../lib/fcm";

const DISMISS_KEY = "konattu_ios_install_dismissed_at";
const HIDE_FOR_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

/**
 * iPhone-specific "Add to Home Screen" hint. iOS Safari has no beforeinstall-
 * prompt event and Web Push requires the PWA to be installed (iOS 16.4+).
 *
 * Renders only on iOS Safari when:
 *   • the user is NOT in standalone mode, AND
 *   • they haven't dismissed within the last 14 days.
 */
export default function IOSInstallBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!iosNeedsInstall()) return;
    const dismissed = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (dismissed && Date.now() - dismissed < HIDE_FOR_MS) return;
    // Slight delay so the banner doesn't fight with the page-load animation.
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="ios-install-banner"
      className="fixed inset-x-3 bottom-20 z-[60] sm:left-auto sm:right-4 sm:bottom-4 sm:w-[380px] rounded-xl border border-red-100 bg-white shadow-xl"
    >
      <div className="flex items-start gap-3 p-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-600 text-white">
          <Plus size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">Install Konattu HRMS on your iPhone</p>
          <p className="mt-0.5 text-xs text-gray-600">
            Tap the <Share2 className="inline h-3.5 w-3.5 mb-0.5" /> <strong>Share</strong> button below, then choose <strong>Add to Home Screen</strong>. After that, open the app from your home screen to enable push notifications.
          </p>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={dismiss}
          className="text-gray-400 hover:text-gray-600"
          data-testid="ios-install-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
